import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fieldError, type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatDate } from '../../lib/format';
import type { TenantDomain } from '../../lib/types';
import {
  Badge,
  Button,
  Card,
  DataTable,
  FormField,
  Input,
  PageHeader,
  Spinner,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';

export function DomainsPage() {
  const [customDomain, setCustomDomain] = useState('');
  const [formError, setFormError] = useState<ParsedApiError | null>(null);
  const queryClient = useQueryClient();
  const domainsQuery = useQuery({ queryKey: ['domains'], queryFn: api.domains.get });

  const mutation = useMutation({
    mutationFn: () => api.domains.update({ domain: customDomain, type: 'custom' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      setCustomDomain('');
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  const domains = domainsQuery.data?.data ?? [];
  const subdomain = domains.find((d) => d.type === 'subdomain');
  const customDomains = domains.filter((d) => d.type === 'custom');

  return (
    <div>
      <PageHeader
        title="Domains"
        description="Your Loctopus subdomain and any custom domain you've connected."
      />
      {domainsQuery.isLoading ? (
        <Spinner />
      ) : domainsQuery.error ? (
        <ErrorSummary error={toParsedError(domainsQuery.error)} />
      ) : (
        <>
          <Card title="Subdomain" className="mb-6">
            {subdomain ? (
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm">{subdomain.domain}</span>
                <Badge tone={subdomain.verifiedAt ? 'success' : 'warning'}>
                  {subdomain.verifiedAt ? 'Verified' : 'Pending'}
                </Badge>
              </div>
            ) : (
              <p className="text-sm text-slate-500">No subdomain provisioned yet.</p>
            )}
          </Card>
          <Card title="Custom domains" className="mb-6">
            <DataTable<TenantDomain>
              columns={[
                {
                  key: 'domain',
                  header: 'Domain',
                  render: (d) => <span className="font-mono text-sm">{d.domain}</span>,
                },
                {
                  key: 'verified',
                  header: 'DNS verified',
                  render: (d) =>
                    d.verifiedAt ? (
                      <Badge tone="success">Verified {formatDate(d.verifiedAt)}</Badge>
                    ) : (
                      <Badge tone="warning">Pending DNS verification</Badge>
                    ),
                },
                {
                  key: 'applePay',
                  header: 'Apple Pay domain',
                  render: (d) =>
                    d.applePayRegisteredAt ? (
                      <Badge tone="success">Registered</Badge>
                    ) : (
                      <Badge tone="neutral">Not registered</Badge>
                    ),
                },
              ]}
              rows={customDomains}
              getRowKey={(d) => d.id}
              emptyMessage="No custom domain connected yet."
            />
          </Card>
          <RequirePermission permission="domains.manage">
            <Card title="Connect a custom domain">
              {formError && <ErrorSummary error={formError} className="mb-3" />}
              <FormField
                label="Domain"
                hint="Point a CNAME at the address the Client Worker (apps/client-api) gives you after saving - DNS instructions aren't in docs/API.md yet"
                error={fieldError(formError, 'domain')}
              >
                <Input
                  value={customDomain}
                  onChange={(e) => setCustomDomain(e.target.value)}
                  placeholder="tickets.yourbrand.com"
                />
              </FormField>
              <Button
                onClick={() => mutation.mutate()}
                disabled={!customDomain.trim() || mutation.isPending}
              >
                {mutation.isPending ? 'Saving...' : 'Connect domain'}
              </Button>
            </Card>
          </RequirePermission>
        </>
      )}
    </div>
  );
}
